//initial redux
import { put, call, takeEvery, fork, all, take, select } from "redux-saga/effects";
import * as router from "react-router-redux";
import { eventChannel, delay } from "redux-saga";
import reportsservice from "../../../../services/reports";
import moment from "moment";
import pointsservice from "../../../../services/points";
import { seriesColor, unitsservice, pointName } from "../../../../utils";
import dateTime from "../../../../utils/dateTime";
import ruleservice from "../../../../services/rules";
import { ar } from "date-fns/esm/locale";
import { actions as seriesColorActions } from "../../../../services/SeriesColor/redux";

const initialState = {};

/////////
//types
export const types = {
	LOAD_DATA: "components/scatterchart/LOAD_DATA"
};

///////////
//reducers
export const reducers = {
	reducer(state = initialState, action) {
		switch (action.type) {
			case types.LOAD_DATA: {
				return state;
			}
			default:
				return state;
		}
	}
};

function* createScatterSeries(chart, xPoint, xData, yPoint, yData, failTimes) {
	
	const colors = seriesColor.getSeriesColors();
	let {colorDictionary, colorIndex} = yield select(x => x.seriesColor);

	let color = colorDictionary[yPoint.name] ? colorDictionary[yPoint.name] : null;
	if(!color) {
		var newColor = seriesColor.getColorForPoint(yPoint.name);
		if(newColor) {
			color = newColor;
		} else {
			color = colors[colorIndex];
			colorDictionary[yPoint.name] = color;
			colorIndex++;
			if(colorIndex >= colors.length) {
				colorIndex = 0;
			}
			yield put(seriesColorActions.newPoint(yPoint.name, color));
		}
	}

	let runTimeSeries = {
		type: "scatter",
		data: [],
		yAxis: getAxisIndex(chart, yPoint.units),
		name: pointName.formatDescription(yPoint),
		color: color,
		pointPadding: 0,
		groupPadding: 0,
		label: yPoint.name,
		borderWidth: 0,
		tooltip: {
			pointFormatter: function() {
				return (
					'<span style="color:{point.color}">\u25CF</span> ' +
					"<b>" +
					Math.floor(this.x) +
					" " +
					unitsservice.stringToSymbol(xPoint.units) +
					" - " +
					Math.floor(this.y) +
					" " +
					unitsservice.stringToSymbol(yPoint.units) +
					"</b><br/>"
				);
			}
		}
	};

	xData.forEach(x => {
		x.x = true;
	});
	yData.forEach(y => {
		y.y = true;
	});

	let aggregate = xData.concat(yData).sort((a, b) => {
		if (a.time < b.time) return -1;
		if (a.time > b.time) return 1;
		return 0;
	});

	let lastXVal;
	let lastYVal;

	runTimeSeries.data = aggregate.reduce((data, next) => {
		if (next.x && next.value !== lastXVal) {
			lastXVal = next.value;
		}
		if (next.y && next.value !== lastYVal) {
			lastYVal = next.value;
		}
		var hasFailed = false;
		for(var ii = 0; ii < failTimes.length; ii++) {
			var ts = new Date(next.timestamp).getTime();
			if(ts >= failTimes[ii][0] && ts <= failTimes[ii][1]) {
				hasFailed = true;
				break;
			}
		}

		if (next.y) {
			if(hasFailed) {
				data.push({x: lastXVal, y: lastYVal, color: "#FF0000"});
			} else {
				data.push([lastXVal, lastYVal]);
			}
		}
		return data;
	}, []);

	return runTimeSeries;
}

function* addScatterSeries(chart, pointDetails, pointData, failTimes) {
	if (!chart.options || pointDetails.length < 2) {
		return;
	}

	const xPoint = pointDetails[0];
	const xPointData = pointData[xPoint.objectID];

	chart.xAxis[0].update({
		title: { text: pointName.formatDescription(xPoint) },
		labels: {
			formatter() {
				return this.value + " " + unitsservice.stringToSymbol(xPoint.units);
			}
		}
	});

	//Remove all series in scatter chart
	while(chart.series.length > 0) {
		chart.series[0].remove(true);
	}

	for (let index = 1; index < pointDetails.length; index++) {
		const point = pointDetails[index];
		const series = yield call(createScatterSeries, chart, xPoint, xPointData, point, pointData[point.objectID], failTimes);
		if (series) {
			chart.addSeries(series);
		}
	}
}

//////////
//sagas
export const sagas = {
	*rootSaga() {
		yield all([this.loadData()]);
	},

	*loadData() {
		yield takeEvery(types.LOAD_DATA, function*({ payload }) {
			const component = payload.component;
			const chart = payload.chart;
			const ruleInstanceId = component.ruleInstanceId;
			const fromDate = moment(payload.timePeriod.startDate);
			const toDate = moment(payload.timePeriod.endDate);

			let pointIds = [];

			for (let index = 0; index < component.inputs.length; index++) {
				const input = component.inputs[index];
				for (let ss = 0; ss < input.values.length; ss++) {
					const value = input.values[ss];
					pointIds.push(value.value);
				}
			}

			const runTimes = yield call(
				ruleservice.GetRuleRunTimes,
				ruleInstanceId,
				fromDate.toDate().toISOString(),
				toDate.toDate().toISOString()
			);
			if (runTimes instanceof Error || !runTimes ) {
				return;
			}
			const pointDetails = yield all(
				pointIds.map(pointId => call(pointsservice.TrendableObjectGetObject, pointId))
			);
			if (pointDetails instanceof Error || !pointDetails) {
				return;
			}
			let pointData = yield call(
				pointsservice.TrendableDataGetData,
				pointDetails.map(x => x.objectID).join(","),
				fromDate,
				toDate
			);
			if (pointData instanceof Error || !pointData) {
				return;
			}

			if (Array.isArray(pointData)) {
				pointData = { [pointIds[0]]: pointData };
			}

			let prevIndexs = {};
			let newData = Object.keys(pointData).reduce((prev, prop) => {
				prev[prop] = [];
				prevIndexs[prop] = 0;
				return prev;
			}, {});

			//now filter out data that is not in any of the ranges that represent the rule's runtimes.
			// create item.time while we are here
			for (let z = 0; z < runTimes.length; z++) {
                let range = runTimes[z];
				range.startDate = range.startDate.getTime();
				range.endDate = range.endDate.getTime();
				for (const prop in newData) {
					if (newData.hasOwnProperty(prop)) {
						const arr = newData[prop];
						let inRange = false;
						for (let index = prevIndexs[prop]; index < pointData[prop].length; index++) {
							const item = pointData[prop][index];
							if (!item.time) {
								item.time = dateTime.getGMTDate(item.timestamp).getTime();
							}
							if (range.startDate <= item.time && item.time < range.endDate) {
								inRange = true;
								arr.push(item);
							} else if (inRange) {
								prevIndexs[prop] = index;
								break;
							}
						}
					}
				}
			}

			for (let index = 0; index < component.inputs.length; index++) {
				const input = component.inputs[index];
				for (let ss = 0; ss < input.values.length; ss++) {
					const value = input.values[ss];
					pointDetails.filter(x => x.objectID === value.value)[0].cov = value.coV;
				}
			}

			if (pointDetails && pointData) {
				var failTimes;
				const outcomePeriods = yield call(reportsservice.GetRunTimesAndFailures, ruleInstanceId, fromDate, toDate, false);
				if(outcomePeriods instanceof Error || !outcomePeriods){
					failTimes = [];
				} else {
					const fails = outcomePeriods.filter(x => x.runState === 3);
					failTimes = fails.map(x => [new Date(x.startSiteLocal).getTime(), new Date(x.endSiteLocal).getTime()]);
				}

				yield call(addScatterSeries, chart, pointDetails, newData, failTimes);
			}
			if(payload.onLoadComplete){
				payload.onLoadComplete();
			}
		});
	}
};

////////
//actions
export const actions = {
	loadData(component, chart, timePeriod, onLoadComplete) {
		return {
			type: types.LOAD_DATA,
			payload: { component, chart, timePeriod, onLoadComplete }
		};
	}
};
function configYAxis(units) {
	let newYAxis = {
		title: {
			text: units,
			align: "high",
			rotation: 0,
			margin: 0,
			y: -30,
			offset: 14,
			style: {
				"font-weight": "600",
				"font-size": "15px"
			}
		},
		labels: {
			style: {
				fontSize: "14px",
				color: "#999"
			}
		}
	};

	if (units == "inSession" ) {
		newYAxis.min = 0;
		newYAxis.max = 1;
		newYAxis.height = "20%";
		newYAxis.top = "80%";
	}

	return newYAxis;
}
//returns teh index of the y index with teh specified units.
//if it doesn't exist it creates a new axis with teh units and returns the index for that axis.
function getAxisIndex(chart, units) {
	if (!chart.options) {
		return;
	}
	let axisIndex = 0;
	let symbol = unitsservice.stringToSymbol(units);
	units = symbol ? symbol : units;
	//if the first yaxis doesnt have a value set then
	if (!chart.yAxis[0].userOptions.title) {
		chart.yAxis[0].update(configYAxis(units));
	} else {
		//search for existing with same units and use that, else add a new axis.
		let axisFound = false;
		for (var ii = 0; ii < chart.yAxis.length; ii++) {
			let wkAxis = chart.yAxis[ii];
			if (wkAxis.userOptions.title.text == units) {
				axisFound = true;
				axisIndex = ii;
				break;
			}
		}
		if (!axisFound) {
			axisIndex = chart.yAxis.length;
			chart.addAxis(configYAxis(units));
		}
	}
	return axisIndex;
}



// WEBPACK FOOTER //
// ./src/components/ReportComponents/Charts/ScatterChart/redux.js